/**
 * Visuels de fiche (notions, migration 187) : frise, pyramide, étapes, bilan à deux colonnes.
 * Données JSON éditées dans l'admin (bloc « Visuel ») ; tout est rendu comme TEXTE (aucun HTML injecté).
 * Couleurs du thème (clair et sombre) ; l'accent suit la thématique (--notion-accent) quand il est défini.
 */

/** Frise : { title?, events: [{ date, label, detail? }] } */
export function Timeline({ data }) {
    const events = Array.isArray(data?.events) ? data.events : [];
    return (
        <figure className="fv fv-timeline">
            {data?.title && <figcaption className="fv-title">{data.title}</figcaption>}
            <ol>
                {events.map((e, i) => (
                    <li key={i}>
                        <span className="fv-timeline__date">{e.date}</span>
                        <span className="fv-timeline__dot" aria-hidden="true" />
                        <span className="fv-timeline__body">
                            <strong>{e.label}</strong>
                            {e.detail && <span>{e.detail}</span>}
                        </span>
                    </li>
                ))}
            </ol>
        </figure>
    );
}

/** Pyramide : { title?, levels: [{ label, detail? }] (du sommet à la base), note? } */
export function Pyramid({ data }) {
    const levels = Array.isArray(data?.levels) ? data.levels : [];
    const n = Math.max(levels.length, 1);
    return (
        <figure className="fv fv-pyramid">
            {data?.title && <figcaption className="fv-title">{data.title}</figcaption>}
            <div className="fv-pyramid__levels">
                {levels.map((l, i) => (
                    <div key={i} className="fv-pyramid__level" style={{ width: `${Math.round(50 + (50 * i) / Math.max(n - 1, 1))}%`, '--fv-i': i, '--fv-n': n }}>
                        <strong>{l.label}</strong>
                        {l.detail && <span>{l.detail}</span>}
                    </div>
                ))}
            </div>
            {data?.note && <p className="fv-note">{data.note}</p>}
        </figure>
    );
}

/** Étapes : { title?, steps: [{ label, actor?, delay?, detail? }] } */
export function Steps({ data }) {
    const steps = Array.isArray(data?.steps) ? data.steps : [];
    return (
        <figure className="fv fv-steps">
            {data?.title && <figcaption className="fv-title">{data.title}</figcaption>}
            <ol>
                {steps.map((s, i) => (
                    <li key={i}>
                        <span className="fv-steps__num">{i + 1}</span>
                        <span className="fv-steps__body">
                            <strong>{s.label}</strong>
                            {(s.actor || s.delay) && (
                                <span className="fv-steps__chips">
                                    {s.actor && <span className="fv-chip">{s.actor}</span>}
                                    {s.delay && <span className="fv-chip fv-chip--time">{s.delay}</span>}
                                </span>
                            )}
                            {s.detail && <span className="fv-steps__detail">{s.detail}</span>}
                        </span>
                    </li>
                ))}
            </ol>
        </figure>
    );
}

/**
 * Bilan à deux colonnes : { title?, left: { title }, right: { title },
 *   rows: [{ left, right, tone? ('stable' | 'circulant' | 'tresorerie') }], results?: [{ label, formula, value? }] }
 */
export function Bilan({ data }) {
    const rows = Array.isArray(data?.rows) ? data.rows : [];
    const results = Array.isArray(data?.results) ? data.results : [];
    return (
        <figure className="fv fv-bilan">
            {data?.title && <figcaption className="fv-title">{data.title}</figcaption>}
            <div className="fv-bilan__grid">
                <div className="fv-bilan__head">{data?.left?.title || 'Emplois'}</div>
                <div className="fv-bilan__head">{data?.right?.title || 'Ressources'}</div>
                {rows.map((r, i) => (
                    <div key={i} className={`fv-bilan__row fv-bilan__row--${r.tone || 'stable'}`} style={{ display: 'contents' }}>
                        <div className={`fv-bilan__cell fv-bilan__cell--${r.tone || 'stable'}`}>{r.left}</div>
                        <div className={`fv-bilan__cell fv-bilan__cell--${r.tone || 'stable'}`}>{r.right}</div>
                    </div>
                ))}
            </div>
            {results.length > 0 && (
                <ul className="fv-bilan__results">
                    {results.map((x, i) => (
                        <li key={i}><strong>{x.label}</strong><span>{x.formula}</span>{x.value && <em>{x.value}</em>}</li>
                    ))}
                </ul>
            )}
        </figure>
    );
}
